import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useFetch, apiPut, apiPost, timeAgo } from "@/lib/shieldx";
import { cn } from "@/lib/utils";
import { PageHeader, Loader, Panel, EmptyState } from "@/components/shieldx/Primitives";
import {
  Bell, ShieldAlert, Siren, Building2, Boxes, KeyRound, FileBarChart, Crosshair,
  HeartPulse, FileLock2, Mail, Plug, CheckCheck,
} from "lucide-react";

const CAT_ICON = {
  Security: ShieldAlert, Incidents: Siren, Organizations: Building2, Assets: Boxes,
  Authorization: KeyRound, Reports: FileBarChart, Simulation: Crosshair, Recovery: HeartPulse,
  Administration: FileLock2, Email: Mail, Integrations: Plug,
};
const SEV_TONE = {
  INFO: "text-sky-400", NOTICE: "text-cyan-400", LOW: "text-emerald-400", MEDIUM: "text-amber-400",
  HIGH: "text-orange-400", CRITICAL: "text-red-400", EMERGENCY: "text-rose-400",
};
const SEVERITIES = ["", "INFO", "NOTICE", "LOW", "MEDIUM", "HIGH", "CRITICAL", "EMERGENCY"];

export default function NotificationCenter() {
  const [cat, setCat] = useState("");
  const [sev, setSev] = useState("");
  const [unread, setUnread] = useState(false);
  const qs = `?${cat ? `category=${cat}&` : ""}${sev ? `severity=${sev}&` : ""}${unread ? "unread_only=true" : ""}`;
  const { data, loading, reload } = useFetch(`/notifications/feed${qs}`, [cat, sev, unread]);
  const navigate = useNavigate();
  if (loading || !data) return <Loader label="Loading notifications" />;

  const open = async (n) => { try { await apiPut(`/notifications/${n.id}/read`, {}); } catch {} navigate(n.deep_link || "/app/command-center"); };
  const ack = async (n, e) => { e.stopPropagation(); try { await apiPost(`/notifications/${n.id}/ack`, {}); toast.success("Acknowledged"); reload(); } catch {} };
  const readAll = async () => { try { await apiPost("/notifications/read-all", {}); toast.success("All marked read"); reload(); } catch {} };

  return (
    <div>
      <PageHeader icon={Bell} title="Notification Center"
        subtitle="Real platform events — role and tenant aware. Nothing here is fabricated."
        actions={<button onClick={readAll} data-testid="notif-read-all" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-200 hover:border-blue-500/50"><CheckCheck className="h-4 w-4" /> Mark all read</button>} />
      <div className="grid gap-5 lg:grid-cols-[220px_1fr]">
        <div className="space-y-1.5">
          <button onClick={() => setCat("")} data-testid="notif-cat-all" className={cn("flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm", !cat ? "bg-blue-600/15 text-blue-300 ring-1 ring-inset ring-blue-500/30" : "text-slate-400 hover:bg-slate-800/60")}>
            <span>All</span><span className="font-mono text-xs">{data.counts.total}</span>
          </button>
          {(data.categories || []).map((c) => {
            const Icon = CAT_ICON[c] || Bell; const n = data.counts.by_category?.[c] || 0;
            return (
              <button key={c} onClick={() => setCat(c)} data-testid={`notif-cat-${c.toLowerCase()}`}
                className={cn("flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm", cat === c ? "bg-blue-600/15 text-blue-300 ring-1 ring-inset ring-blue-500/30" : "text-slate-400 hover:bg-slate-800/60")}>
                <span className="flex items-center gap-2"><Icon className="h-4 w-4" /> {c}</span>
                <span className="font-mono text-xs">{n}</span>
              </button>
            );
          })}
        </div>
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <select value={sev} onChange={(e) => setSev(e.target.value)} data-testid="notif-sev-filter" className="h-9 rounded-lg border border-slate-800 bg-slate-900/70 px-2.5 text-xs text-slate-300 outline-none">
              {SEVERITIES.map((s) => <option key={s} value={s}>{s || "All severities"}</option>)}
            </select>
            <button onClick={() => setUnread((u) => !u)} data-testid="notif-unread-toggle" className={cn("h-9 rounded-lg border px-3 text-xs", unread ? "border-blue-500/50 text-blue-300" : "border-slate-800 text-slate-400")}>Unread only</button>
          </div>
          <Panel>
            {data.notifications?.length ? (
              <div className="divide-y divide-slate-800/70">
                {data.notifications.map((n) => {
                  const Icon = CAT_ICON[n.category] || Bell;
                  return (
                    <div key={n.id} data-testid={`notif-row-${n.id}`} onClick={() => open(n)}
                      className={cn("flex cursor-pointer items-start gap-3 py-3 transition-colors hover:bg-slate-800/30", n.status === "UNREAD" && "bg-slate-800/20")}>
                      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className={cn("font-mono text-[10px] font-bold uppercase tracking-widest", SEV_TONE[n.severity])}>{n.severity}</span>
                          <span className="font-mono text-[10px] uppercase tracking-widest text-slate-600">{n.category}</span>
                          <span className="text-[10px] text-slate-600">{timeAgo(n.timestamp)}</span>
                          {n.status !== "UNREAD" && <span className="rounded border border-slate-700 px-1 font-mono text-[9px] text-slate-500">{n.status}</span>}
                        </div>
                        <p className="mt-0.5 truncate text-sm font-medium text-slate-200">{n.title}</p>
                        <p className="truncate text-xs text-slate-500">{n.summary}</p>
                        <p className="mt-0.5 font-mono text-[10px] text-slate-600">{n.related_id}</p>
                      </div>
                      <button onClick={(e) => ack(n, e)} data-testid={`notif-ack-${n.id}`} className="shrink-0 rounded-lg border border-slate-700 px-2 py-1 text-[10px] text-slate-400 hover:border-blue-500/50">Ack</button>
                    </div>
                  );
                })}
              </div>
            ) : <EmptyState title="NO NEW NOTIFICATIONS" desc="You're all caught up." icon={Bell} />}
          </Panel>
        </div>
      </div>
    </div>
  );
}
